// --- Owner Auction View: Mobile-friendly bidding interface ---
import React from 'react';
import { Gavel, Timer, User, TrendingUp, Wallet, CheckCircle, XCircle, Clock, Star, LogOut } from 'lucide-react';
import type { Player, Team, AppUser } from '../types';
import { useAuction } from '../hooks/useAuction';
import { formatCurrency } from '../utils/helpers';

interface OwnerAuctionViewProps {
  players: Player[];
  teams: Team[];
  currentUser: AppUser;
  shortlists?: Record<string, string[]>;
  onLogout?: () => void;
}

const OwnerAuctionView: React.FC<OwnerAuctionViewProps> = ({ players, teams, currentUser, shortlists = {}, onLogout }) => {
  const {
    auction,
    isLoading,
    placeBid,
    canBid,
    myTeam
  } = useAuction({ players, teams, currentUser });

  const [showCustomBid, setShowCustomBid] = React.useState(false);
  const [customBidAmount, setCustomBidAmount] = React.useState<string>('');

  const isShortlisted = auction.currentPlayer && myTeam
    ? (shortlists[myTeam.id] || []).includes(auction.currentPlayer.id)
    : false;

  // Update custom bid amount default when current bid changes
  React.useEffect(() => {
    if (auction.isActive && auction.currentBid > 0 && auction.bidIncrement > 0) {
       // Propose next valid bid
       setCustomBidAmount((auction.currentBid + auction.bidIncrement).toString());
    }
  }, [auction.isActive, auction.currentBid, auction.bidIncrement]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-purple-900 to-slate-900 flex items-center justify-center p-4">
        <div className="text-center">
          <Gavel className="w-12 h-12 text-yellow-400 animate-bounce mx-auto mb-4" />
          <p className="text-white text-lg">Connecting to Auction...</p>
        </div>
      </div>
    );
  }

  // No active auction
  if (!auction.isActive) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-800 to-slate-900 flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <Clock className="w-16 h-16 text-slate-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white mb-2">No Active Auction</h2>
          <p className="text-slate-400">
            The auction has not started yet. You'll see the bidding interface here when the auctioneer starts the session.
          </p>
          {myTeam && (
            <div className="mt-6 p-4 bg-white/10 rounded-xl">
              <p className="text-white/70 text-sm">Your Team</p>
              <p className="text-white font-bold text-lg">{myTeam.name}</p>
              <p className="text-green-400 font-semibold">{formatCurrency(myTeam.currentBalance)} pts</p>
            </div>
          )}
        </div>
      </div>
    );
  }

  const nextBidAmount = auction.currentBid + auction.bidIncrement;
  const isLeading = auction.currentBidder?.id === myTeam?.id;
  const canAfford = myTeam ? myTeam.currentBalance >= nextBidAmount : false;
  const maxAffordableMultiplier = myTeam
    ? Math.floor((myTeam.currentBalance - auction.currentBid) / auction.bidIncrement)
    : 0;
  const maxBidAmount = maxAffordableMultiplier > 0
    ? auction.currentBid + auction.bidIncrement * maxAffordableMultiplier
    : nextBidAmount;

  const handleBid = (amount?: number) => {
    if (!canBid || isLeading) return;
    placeBid(amount).then(success => {
      if (success) {
        setShowCustomBid(false);
      }
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-purple-900 to-slate-900">
      {/* Header */}
      <div className="bg-black/30 backdrop-blur-sm border-b border-white/10 sticky top-0 z-10">
        <div className="px-4 py-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-2">
            <Gavel className="w-6 h-6 text-yellow-400" />
            <span className="text-white font-semibold">Live Auction</span>
            <span className="px-2 py-0.5 bg-green-500 text-white text-xs rounded-full animate-pulse">LIVE</span>
          </div>
          {myTeam && (
            <div className="flex items-center gap-2 text-sm sm:text-base text-white/80">
              <span className="font-semibold text-white truncate max-w-[12ch] sm:max-w-none">{myTeam.name}</span>
              <div className="flex items-center gap-1 px-2 py-1 rounded-lg bg-white/5 border border-white/10">
                <Wallet className="w-4 h-4 text-green-400" />
                <span className="text-green-400 font-bold">{formatCurrency(myTeam.currentBalance)}</span>
              </div>
            </div>
          )}
          {onLogout && (
            <button
              onClick={onLogout}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-white/20 text-white/80 hover:text-white hover:border-white/40 transition"
            >
              <LogOut className="w-4 h-4" />
              <span className="text-sm font-semibold">Logout</span>
            </button>
          )}
        </div>
      </div>

      <div className="p-4 space-y-4 pb-28 max-w-3xl mx-auto">
        {/* Current Player Card */}
        {auction.currentPlayer ? (
          <div className="bg-white/10 backdrop-blur-sm rounded-2xl overflow-hidden relative">

            {/* Shortlist Badge (Star) */}
            {isShortlisted && (
              <div className="absolute top-4 right-4 z-10 bg-yellow-400/90 text-yellow-900 px-3 py-1 rounded-full flex items-center gap-1 shadow-lg animate-pulse">
                <Star className="w-4 h-4 fill-yellow-900" />
                <span className="text-xs font-bold uppercase tracking-wider">Shortlisted</span>
              </div>
            )}

            {/* Player Header */}
            <div className="bg-gradient-to-r from-indigo-600 to-purple-600 p-6 text-center">
              {auction.currentPlayer.details?.photo_url ? (
                <img
                  src={auction.currentPlayer.details.photo_url}
                  alt=""
                  className="w-24 h-24 rounded-full mx-auto mb-3 border-4 border-white/30 object-cover"
                />
              ) : (
                <div className="w-24 h-24 rounded-full mx-auto mb-3 bg-white/20 flex items-center justify-center">
                  <User className="w-12 h-12 text-white/50" />
                </div>
              )}
              <h2 className="text-2xl font-bold text-white">{auction.currentPlayer.name}</h2>
              <p className="text-white/70">
                {auction.currentPlayer.details?.player_gender || 'Unknown'} • {auction.currentPlayer.details?.playing_side || 'Unknown'} Side
              </p>
            </div>

            {/* Status */}
            {auction.status === 'sold' && (
              <div className="bg-green-500 py-3 px-4 text-center">
                <CheckCircle className="w-6 h-6 inline mr-2" />
                <span className="text-white font-bold text-lg">SOLD to {auction.currentBidder?.name}!</span>
              </div>
            )}
            {auction.status === 'unsold' && (
              <div className="bg-red-500 py-3 px-4 text-center">
                <XCircle className="w-6 h-6 inline mr-2" />
                <span className="text-white font-bold text-lg">UNSOLD</span>
              </div>
            )}

            {/* Timer & Bid Info */}
            {(auction.status === 'bidding' || auction.status === 'paused' || auction.status === 'idle') && (
              <div className="p-4 space-y-4">
                {/* Timer */}
                {auction.status === 'bidding' && (
                  <div className={`text-center py-4 rounded-xl ${
                    auction.timer <= 3 ? 'bg-red-500/30 animate-pulse' : 'bg-yellow-500/20'
                  }`}>
                    <Timer className={`w-8 h-8 mx-auto mb-1 ${auction.timer <= 3 ? 'text-red-400' : 'text-yellow-400'}`} />
                    <span className={`text-5xl font-bold ${auction.timer <= 3 ? 'text-red-400' : 'text-yellow-400'}`}>
                      {auction.timer}
                    </span>
                    <span className="text-white/70 text-lg ml-1">sec</span>
                  </div>
                )}
                {auction.status === 'paused' && (
                  <div className="text-center py-4 rounded-xl bg-orange-500/20">
                    <span className="text-orange-400 font-bold text-xl">PAUSED</span>
                  </div>
                )}
                {auction.status === 'idle' && (
                  <div className="text-center py-4 rounded-xl bg-slate-500/20">
                    <span className="text-slate-400 font-semibold">Waiting for bidding to start...</span>
                  </div>
                )}

                {/* Current Bid */}
                <div className="bg-green-600/20 rounded-xl p-4 text-center">
                  <p className="text-white/70 text-sm mb-1">Current Bid</p>
                  <p className="text-4xl font-bold text-green-400">{formatCurrency(auction.currentBid)}</p>
                  {auction.currentBidder && (
                    <p className="text-white/80 mt-1">
                      by <span className={`font-semibold ${isLeading ? 'text-green-400' : ''}`}>
                        {isLeading ? 'YOU' : auction.currentBidder.name}
                      </span>
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 text-center">
            <p className="text-white/70">Waiting for player selection...</p>
          </div>
        )}

      </div>

      {/* Bid Button - Fixed at bottom for mobile */}
      {auction.currentPlayer && auction.status === 'bidding' && myTeam && (
        <div className="fixed bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/85 to-black/30 space-y-3 backdrop-blur-sm">

          {/* Custom Bid Toggle */}
          {canBid && !isLeading && (
            <div className="flex justify-center">
               <button
                 onClick={() => setShowCustomBid(!showCustomBid)}
                 className="px-3 py-1.5 rounded-full bg-white/10 text-white text-sm font-semibold border border-white/20 shadow-sm hover:bg-white/15"
               >
                 {showCustomBid ? 'Hide custom amount' : 'Bid a custom amount'}
               </button>
            </div>
          )}

          {/* Custom Bid Input */}
          {showCustomBid && canBid && !isLeading && (
            <div className="bg-white/10 backdrop-blur p-3 rounded-2xl space-y-3 border border-white/10">
              <div className="flex items-center justify-between text-xs text-white/60">
                <span>Custom bid</span>
                <span>Minimum {formatCurrency(nextBidAmount)}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-white font-bold text-lg">$</span>
                <input
                  inputMode="numeric"
                  type="number"
                  min={nextBidAmount}
                  value={customBidAmount}
                  onChange={(e) => setCustomBidAmount(e.target.value)}
                  className="w-full bg-transparent text-white font-bold text-2xl outline-none border-b border-white/30 focus:border-white"
                  placeholder={`${nextBidAmount}`}
                />
                <button
                  onClick={() => {
                    const val = parseInt(customBidAmount);
                    if (!isNaN(val)) {
                      handleBid(val);
                    }
                  }}
                  className="px-4 py-2 bg-yellow-500 text-yellow-900 font-bold rounded-lg hover:bg-yellow-400"
                >
                  Bid
                </button>
              </div>
              <div className="grid grid-cols-3 gap-2 text-sm">
                {[1, 2, 3].map(multiplier => {
                  const suggested = auction.currentBid + auction.bidIncrement * multiplier;
                  return (
                    <button
                      key={multiplier}
                      onClick={() => setCustomBidAmount(suggested.toString())}
                      className="w-full py-2 rounded-xl bg-white/5 border border-white/10 text-white/80 hover:bg-white/15"
                    >
                      +{multiplier}x ({formatCurrency(suggested)})
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Quick Bid Chips */}
          <div className="flex flex-wrap gap-2">
            {[1, 2, 3].map(multiplier => {
              const suggested = auction.currentBid + auction.bidIncrement * multiplier;
              const disabled = !canBid || isLeading || !myTeam || suggested > myTeam.currentBalance;

              return (
                <button
                  key={multiplier}
                  onClick={() => handleBid(suggested)}
                  disabled={disabled}
                  className={`px-3 py-2 rounded-xl border text-sm font-semibold transition-all ${
                    disabled
                      ? 'bg-white/5 border-white/10 text-white/30 cursor-not-allowed'
                      : 'bg-white/10 border-white/20 text-white hover:bg-white/20 active:scale-95'
                  }`}
                >
                  +{multiplier}x ({formatCurrency(suggested)})
                </button>
              );
            })}
            {maxAffordableMultiplier > 0 && (
              <button
                onClick={() => handleBid(maxBidAmount)}
                disabled={!canBid || isLeading}
                className={`px-3 py-2 rounded-xl border text-sm font-semibold transition-all ${
                  !canBid || isLeading
                    ? 'bg-white/5 border-white/10 text-white/30 cursor-not-allowed'
                    : 'bg-yellow-400/90 border-yellow-200/60 text-yellow-900 hover:bg-yellow-300 active:scale-95'
                }`}
              >
                Max ({formatCurrency(maxBidAmount)})
              </button>
            )}
          </div>

          <button
            onClick={() => handleBid(undefined)}
            disabled={!canBid}
            className={`w-full py-5 rounded-2xl font-bold text-xl transition-all transform active:scale-95 ${
              isLeading
                ? 'bg-green-600 text-white cursor-default'
                : canBid
                  ? 'bg-indigo-600 text-white hover:bg-indigo-700 shadow-lg shadow-indigo-500/50'
                  : 'bg-gray-600 text-gray-400 cursor-not-allowed'
            }`}
          >
            {isLeading ? (
              <span className="flex items-center justify-center gap-2">
                <CheckCircle className="w-6 h-6" /> You're Leading!
              </span>
            ) : canBid ? (
              <span className="flex items-center justify-center gap-2">
                <TrendingUp className="w-6 h-6" /> Bid {formatCurrency(nextBidAmount)}
              </span>
            ) : !canAfford ? (
              <span>Insufficient Balance</span>
            ) : (
              <span>Cannot Bid Now</span>
            )}
          </button>
          {!isLeading && canBid && (
            <p className="text-center text-white/60 text-xs sm:text-sm mt-1">
              Tap to place bid • Balance after: {formatCurrency(myTeam.currentBalance - nextBidAmount)}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default OwnerAuctionView;
